// src/pages/vendor/Payouts.jsx
import React, { useState, useEffect } from 'react';
import { vendorApi } from '../../api/vendorApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, CheckCircle, Cancel, Pending, Edit, DeleteOutline } from '@mui/icons-material';

const Payouts = () => {
  const [balance, setBalance] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState(null);

  // Edit Payout State
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.getPayouts();
      
      if (response) {
        const extractedBalance = response.balance ?? response.totalBalance ?? response.availableBalance ?? 0;
        const extractedHistory = response.history ?? response.payouts ?? response.data ?? [];
        
        setBalance(typeof extractedBalance === 'string' ? parseFloat(extractedBalance) || 0 : extractedBalance);
        setPayouts(Array.isArray(extractedHistory) ? extractedHistory : []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRequestPayout = async () => {
    const amountNum = parseFloat(amount);
    if (!amount || amountNum <= 0) return;
    if (amountNum > balance) {
      setError('Insufficient available balance');
      return;
    }

    try {
      await vendorApi.requestPayout({ amount: amountNum });
      setSuccess('Withdrawal request submitted successfully!');
      setOpenDialog(false);
      setAmount('');
      fetchData(); // This fetches the NEW updated balance immediately
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request payout');
    }
  };

  // --- EDIT AMOUNT ---
  const handleOpenEdit = (payout) => {
    setEditingPayout(payout);
    setEditAmount(payout.amount.toString());
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    const newAmount = parseFloat(editAmount);
    if (!newAmount || newAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (newAmount > balance) {
      setError('Insufficient available balance');
      return;
    }

    try {
      await vendorApi.updatePayoutAmount(editingPayout.id, newAmount);
      setSuccess('Payout amount updated successfully!');
      setOpenEditDialog(false);
      setEditingPayout(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payout amount');
    }
  };

  // --- DELETE / CANCEL REQUEST ---
  const handleCancelPayout = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payout request?')) return;

    try {
      await vendorApi.cancelPayout(id);
      setSuccess('Payout request cancelled successfully.');
      fetchData(); // Immediately frees up the balance
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel payout request');
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold">Payouts & Withdrawals</Typography>
        <Typography variant="body2" color="text.secondary">Track your earnings and request payouts</Typography>
      </Paper>

      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white', p: 2, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption">AVAILABLE BALANCE</Typography>
            <Typography variant="h3" fontWeight="bold">${balance.toFixed(2)}</Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => setOpenDialog(true)} 
            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
            disabled={loading || balance <= 0} // ⛔ Disables button if balance is 0
          >
            Request Withdrawal
          </Button>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ borderRadius: 3, p: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Withdrawal History</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!payouts || payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary">No payout history</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((p, index) => (
                  <TableRow key={p.id || index}>
                    <TableCell>{p.requestedAt ? new Date(p.requestedAt).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        ${(typeof p.amount === 'string' ? parseFloat(p.amount) : (p.amount || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{p.method || 'Bank Transfer'}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={p.status === 'completed' ? <CheckCircle /> : p.status === 'pending' ? <Pending /> : <Cancel />}
                        label={p.status || 'pending'} 
                        size="small" 
                        color={p.status === 'completed' ? 'success' : p.status === 'pending' ? 'warning' : 'error'} 
                      />
                    </TableCell>
                    <TableCell align="right">
                      {/* Only show actions for pending requests */}
                      {p.status === 'pending' && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit Amount">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenEdit(p)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Cancel Request">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleCancelPayout(p.id)}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Request Withdrawal Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2">Your current balance: <strong>${balance.toFixed(2)}</strong></Typography>
            <TextField 
              fullWidth 
              type="number" 
              label="Amount (ETB)" 
              value={amount} 
              onChange={(e) => setAmount(e.target.value)} 
              disabled={balance <= 0}
              helperText={balance <= 0 ? "You have no available balance to withdraw." : ""}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRequestPayout} 
            disabled={balance <= 0 || !amount || parseFloat(amount) > balance}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Amount Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Payout Amount</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2">
              Current request: <strong>${(typeof editingPayout?.amount === 'string' ? parseFloat(editingPayout?.amount) : (editingPayout?.amount || 0)).toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2">Your current balance: <strong>${balance.toFixed(2)}</strong></Typography>
            <TextField 
              fullWidth 
              type="number" 
              label="New Amount (ETB)" 
              value={editAmount} 
              onChange={(e) => setEditAmount(e.target.value)} 
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={balance <= 0 || !editAmount || parseFloat(editAmount) > balance}>Update Amount</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;